import { capitalize } from "es-toolkit";
import type { ReactNode } from "react";
import {
  LuAngry,
  LuAnnoyed,
  LuAtSign,
  LuChartColumn as LuBarChart2,
  LuCircleDollarSign,
  LuCoins,
  LuDollarSign,
  LuGem,
  LuHandHelping as LuHelpingHand,
  LuHeart,
  LuLandmark,
  LuLink,
  LuListTodo,
  LuLockOpen as LuUnlock,
  LuMail,
  LuMailOpen,
  LuMails,
  LuMegaphone,
  LuMessageCircle,
  LuMessageSquare,
  LuNewspaper,
  LuPiggyBank,
  LuQuote,
  LuReceipt,
  LuRepeat,
  LuReply,
  LuSkull,
  LuSmile,
  LuThumbsDown,
  LuThumbsUp,
  LuUserPlus,
  LuUsers,
  LuView,
  LuWallet,
} from "react-icons/lu";

import type { FocusNotification } from "@/backend/focus";
import {
  NotificationSubCategory,
  ReferralNotificationSubcategory,
} from "@/backend/focus";
import {
  APP_ROUTES,
  EMOJIS,
  MIN_WHALE_USD_CENTS_BALANCE,
} from "@/utils/constants";
import {
  formatDecimalValue,
  formatUSDCents,
  nanosToInteger,
  tokenNanosToInteger,
} from "@/utils/currency";
import { abbreviateNumber } from "@/utils/helpers";
import {
  buildDMThreadIdentifier,
  sanitizeMessagingGroupDisplayName,
} from "@/utils/messages";
import type { NotificationReactionType } from "@/utils/reactions-utils";
import {
  REACTION_EMOJI_NOTIFICATION_MAP,
  REACTION_EMOJI_UTF8_MAP,
} from "@/utils/reactions-utils";
import { getUserDisplayName } from "@/utils/usernames";

export const DEFAULT_DTO = {
  usdPurchaseAmount: "not implemented yet",
  usdOfferAmount: "not implemented yet",
  numAcceptedOffers: "not implemented yet",
  numViewsEstimated: "not implemented yet",
};

export const buildNotificationData = (
  originalNotification: FocusNotification,
) => {
  const notification = structuredClone(originalNotification);
  const notificationExtraData = notification.Notification?.ExtraData as
    | Record<string, unknown>
    | undefined;

  if (notificationExtraData) {
    (["AccessGroupName", "GroupChatName"] as const).forEach((key) => {
      const value = notificationExtraData[key];

      if (typeof value === "string") {
        notificationExtraData[key] = sanitizeMessagingGroupDisplayName(value);
      }
    });
  }

  (["ActorUser", "User"] as const).forEach((key) => {
    const {
      Username = "",
      DisplayName = "",
      TotalWealthUsdCents = 0,
      PublicKey = "",
    } = notification[key] || {};

    notification[key] = {
      Username,
      DisplayName:
        DisplayName ||
        getUserDisplayName({
          username: Username,
          publicKey: PublicKey,
          extraData: { DisplayName },
        }),
      TotalWealthUsdCents,
      PublicKey,
    };
  });

  const { Notification, User, ActorUser } = notification;

  return {
    ...DEFAULT_DTO,
    ...originalNotification,
    Notification,
    ActorUser,
    User,
    username: ActorUser?.Username,
  };
};

export const resolveArrayTemplate = (
  template: (string | null)[] | string,
  data: ReactNode[],
) => {
  if (typeof template === "string") {
    return template;
  }
  return template.map((item) => (item === null ? data.shift() : item));
};

const getGroupChatNotificationSuffix = (
  groupChatName?: string | null,
  style: "called" | "quoted" = "called",
) => {
  const sanitizedGroupChatName =
    sanitizeMessagingGroupDisplayName(groupChatName);

  if (!sanitizedGroupChatName) {
    return style === "quoted" ? "the group chat" : "a new group chat";
  }

  return style === "quoted"
    ? `the "${sanitizedGroupChatName}" group chat`
    : `a new group chat called "${sanitizedGroupChatName}"`;
};

export const PushNotificationActionIcons = {
  ActionViewProfile: LuUsers,
  ActionGoToMessages: LuMail,
  ActionReply: LuReply,
  ActionGoToWallet: LuWallet,
  ActionOpenApp: LuLink,
  ActionViewPost: LuView,
  ActionViewEarnings: LuLandmark,
  ActionTrade: LuDollarSign,
};

const POST_NOTIFICATION_SUBCATEGORIES = new Set<NotificationSubCategory>([
  NotificationSubCategory.MENTION,
  NotificationSubCategory.POST_REPLY,
  NotificationSubCategory.POST_QUOTE_REPOST,
  NotificationSubCategory.UNLOCKABLE_CONTENT,
  NotificationSubCategory.POST_TIP,
  NotificationSubCategory.WHALE_POST_VIEW,
  NotificationSubCategory.POST_VIEW_THRESHOLD,
  NotificationSubCategory.POST_REPOST,
  NotificationSubCategory.REACTION_ANGRY,
  NotificationSubCategory.REACTION_ASTONISHED,
  NotificationSubCategory.REACTION_DISLIKE,
  NotificationSubCategory.REACTION_LAUGH,
  NotificationSubCategory.REACTION_LIKE,
  NotificationSubCategory.REACTION_LOVE,
  NotificationSubCategory.FOLLOWEE_NEW_POST,
  NotificationSubCategory.SUBSCRIBEE_NEW_POST,
]);

const DIRECT_MESSAGE_NOTIFICATION_SUBCATEGORIES =
  new Set<NotificationSubCategory>([
    NotificationSubCategory.PAID_MESSAGE,
    NotificationSubCategory.UNPAID_MESSAGE,
    NotificationSubCategory.NEW_DIRECT_PAID_MESSAGE,
    NotificationSubCategory.NEW_DIRECT_UNPAID_MESSAGE,
    NotificationSubCategory.NEW_DIRECT_PAID_MESSAGE_REPLY,
    NotificationSubCategory.NEW_DIRECT_UNPAID_MESSAGE_REPLY,
    NotificationSubCategory.PAID_MESSAGE_REDEMPTION,
  ]);

const THREAD_IDENTIFIER_NOTIFICATION_SUBCATEGORIES =
  new Set<NotificationSubCategory>([
    ...DIRECT_MESSAGE_NOTIFICATION_SUBCATEGORIES,
    NotificationSubCategory.ADDED_GROUP_CHAT,
    NotificationSubCategory.ADDED_TO_GROUP_CHAT,
    NotificationSubCategory.NEW_GROUP_PAID_MESSAGE,
    NotificationSubCategory.NEW_GROUP_UNPAID_MESSAGE,
    NotificationSubCategory.NEW_GROUP_PAID_MESSAGE_REPLY,
    NotificationSubCategory.NEW_GROUP_UNPAID_MESSAGE_REPLY,
  ]);

const buildMessagesHref = (threadId: string) => {
  return `${APP_ROUTES.messages}?${new URLSearchParams({
    threadId,
  }).toString()}`;
};

const getNotificationThreadIdentifier = (notification: FocusNotification) => {
  const threadIdentifier =
    notification.Notification.ExtraData?.ThreadIdentifier;

  return typeof threadIdentifier === "string" && threadIdentifier
    ? threadIdentifier
    : null;
};

export const resolveNotificationHref = (
  notification: FocusNotification,
): string | null => {
  const {
    Notification: { PostHashHex, Subcategory },
    ActorUser,
    User,
  } = notification;

  if (POST_NOTIFICATION_SUBCATEGORIES.has(Subcategory) && PostHashHex) {
    return `/post/${PostHashHex}`;
  }

  if (THREAD_IDENTIFIER_NOTIFICATION_SUBCATEGORIES.has(Subcategory)) {
    const threadIdentifier = getNotificationThreadIdentifier(notification);

    if (threadIdentifier) {
      return buildMessagesHref(threadIdentifier);
    }
  }

  if (DIRECT_MESSAGE_NOTIFICATION_SUBCATEGORIES.has(Subcategory)) {
    if (!User?.PublicKey || !ActorUser?.PublicKey) {
      return null;
    }

    return buildMessagesHref(
      buildDMThreadIdentifier(User.PublicKey, ActorUser.PublicKey),
    );
  }

  return null;
};

export const getReactionNotificationEmoji = (
  reaction: NotificationReactionType,
  _: (value: string | TemplateStringsArray, possessive?: boolean) => string,
) => {
  return (
    <div className="flex items-center gap-1">
      <div className="flex origin-bottom items-center text-center">
        reacted {/* Don't use next image, it'll fail for push notifications */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={REACTION_EMOJI_NOTIFICATION_MAP[reaction]}
          alt={REACTION_EMOJI_UTF8_MAP[reaction]}
          width={32}
          height={32}
        />
        <div> on {_("your", true)} post!</div>
      </div>
    </div>
  );
};

type ActionObject = {
  action: string;
  title: string;
  icon: string;
};

type CommonActions = {
  [key: string]: (notification: FocusNotification) => ActionObject;
};

const CommonPushNotificationActions: CommonActions = {
  OpenApp: () => ({
    action: "/",
    title: "Open App",
    icon: "/generated/notification-action-icons/ActionOpenApp-light.png",
  }),
  ViewProfile: ({ ActorUser }) => ({
    action: `/${ActorUser.Username}`,
    title: "View Profile",
    icon: `/generated/notification-action-icons/ActionViewProfile-light.png`,
  }),
  ReplyMessage: ({ ActorUser, User }) => ({
    action: buildMessagesHref(
      buildDMThreadIdentifier(User.PublicKey, ActorUser.PublicKey),
    ),
    title: "Reply",
    icon: `/generated/notification-action-icons/ActionReply-light.png`,
  }),
  GoToMessages: () => ({
    action: `/messages`,
    title: "Go to Messages",
    icon: `/generated/notification-action-icons/ActionGoToMessages-light.png`,
  }),
  GoToWallet: () => ({
    action: `/wallet`,
    title: "Go to Wallet",
    icon: `/generated/notification-action-icons/ActionGoToWallet-light.png`,
  }),
  ViewPost: ({ Notification }) => ({
    action: `/post/${Notification.PostHashHex}`,
    title: "View Post",
    icon: `/generated/notification-action-icons/ActionViewPost-light.png`,
  }),
  ReplyComment: ({ Notification }) => ({
    action: `/post/${Notification.PostHashHex}`,
    title: "Reply",
    icon: `/generated/notification-action-icons/ActionReply-light.png`,
  }),
  ViewComment: ({ Notification }) => ({
    action: `/post/${Notification.PostHashHex}`,
    title: "View Comment",
    icon: `/generated/notification-action-icons/ActionViewPost-light.png`,
  }),
  ViewRePost: ({ Notification }) => ({
    action: `/post/${Notification.PostHashHex}`,
    title: "View Repost",
    icon: `/generated/notification-action-icons/ActionViewPost-light.png`,
  }),
  GoToTrade: () => ({
    action: `/trade`,
    title: "Go to Trade",
    icon: `/generated/notification-action-icons/ActionTrade-light.png`,
  }),
  ViewEarnings: () => ({
    action: `/earnings`,
    title: "View Earnings",
    icon: `/generated/notification-action-icons/ActionViewEarnings-light.png`,
  }),
};

export const ReferralNotificationSubcategoryMessagesMap = {
  [ReferralNotificationSubcategory.BOUNTY_REWARD_CLAIMED]: [
    "claimed a bounty reward and earned ",
    null,
    null,
  ],
  [ReferralNotificationSubcategory.NEW_SUBSCRIPTION]: [
    "subscribed and earned ",
    null,
    null,
  ],
  [ReferralNotificationSubcategory.UNLOCKABLE_CONTENT]: [
    "unlocked content and earned ",
    null,
    null,
  ],
  [ReferralNotificationSubcategory.COIN_TRADING_FEE]: [
    "traded coins and earned ",
    null,
    null,
  ],
  [ReferralNotificationSubcategory.POST_TIP]: [
    "received a tip on their post, and ",
    null,
    "earned ",
    null,
  ],
  [ReferralNotificationSubcategory.NEW_DIRECT_PAID_MESSAGE]: [
    "sent a direct paid message and earned ",
    null,
    null,
  ],
  [ReferralNotificationSubcategory.PAID_MESSAGE_REDEMPTION]: [
    "earned ",
    null,
    null,
    "in paid message fees for referring ",
    null,
  ],
  [ReferralNotificationSubcategory.COIN_PURCHASE_PLACED]: [],
  [ReferralNotificationSubcategory.COIN_SALE_PLACED]: [],
  [ReferralNotificationSubcategory.COIN_PURCHASE_FILLED]: [],
  [ReferralNotificationSubcategory.COIN_SALE_FILLED]: [],
};

const createRelativeUsernameHelper =
  (username: string | null) =>
  (strings: string | TemplateStringsArray, possessive = false) =>
    (username && `${username}${possessive ? "'s" : ""}`) ||
    (typeof strings === "string" ? strings : strings[0]);

export const getNotificationSubCategoryResults = (
  notificationData: FocusNotification,
  options: { relativeUsername: string | null } = { relativeUsername: null },
) => {
  const { relativeUsername } = options;
  const _ = createRelativeUsernameHelper(relativeUsername);
  const {
    Notification: {
      Subcategory,
      AmountUsdCents = 0,
      AmountNanosHex = 0x0,
      ExtraData,
      PostHashHex,
      Post,
    },
    ActorUser,
    // @ts-expect-error not implemented yet
    usdPurchaseAmount,
    // @ts-expect-error not implemented yet
    usdOfferAmount,
    // @ts-expect-error not implemented yet
    numAcceptedOffers,
    // @ts-expect-error not implemented yet
    numViewsEstimated,
    User,
  } = notificationData;

  const {
    PostViewCount = 0,
    Quantity = 0,
    PriceUsd = 0,
    QuantityBought = 0,
    QuantitySold = 0,
  } = ExtraData || {};

  const isActorWhale =
    (ActorUser?.TotalWealthUsdCents ?? 0) > MIN_WHALE_USD_CENTS_BALANCE;
  const actorWhaleIdentifier = isActorWhale ? EMOJIS.whale : "";

  switch (Subcategory) {
    case NotificationSubCategory.REFERRAL_COIN_TRADING_FEE:
      return {
        title: `${formatUSDCents(AmountUsdCents)} coin trading fee`,
        body: (
          <>
            {_`You`} received{" "}
            <span className="font-mono text-emerald-400">
              {formatUSDCents(AmountUsdCents)}
            </span>{" "}
            in coin trading fees for referring {ActorUser.DisplayName}$
            {actorWhaleIdentifier}!
          </>
        ),
        push: {
          title: `You earned ${formatUSDCents(AmountUsdCents)} in trading fees!`,
          body: `You earned ${formatUSDCents(AmountUsdCents)} in trading fees on your token just now, @${User.DisplayName}!`,
          actions: [
            CommonPushNotificationActions.ViewEarnings(notificationData),
            CommonPushNotificationActions.GoToTrade(notificationData),
          ],
        },
      };

    case NotificationSubCategory.REFERRAL:
      return {
        title: `${formatUSDCents(AmountUsdCents)} referral bonus`,
        body: (
          <>
            claimed their joining bounty. {_`You`} were paid{" "}
            <span className="font-mono text-emerald-400">
              {formatUSDCents(AmountUsdCents)}
            </span>{" "}
            for referring them!
          </>
        ),
        push: null,
      };

    case NotificationSubCategory.PAID_MESSAGE:
      return {
        title: `${formatUSDCents(AmountUsdCents)} paid message`,
        body: (
          <>
            sent {_`you`} a paid message for{" "}
            <span className="font-mono text-emerald-400">
              {formatUSDCents(AmountUsdCents)}
            </span>
            !
          </>
        ),
        push: null,
      };

    case NotificationSubCategory.NEW_SUBSCRIPTION:
      return {
        title: "+1 subscriber",
        body: (
          <>
            {AmountUsdCents > 0 ? (
              <>
                subscribed to {_("your", true)} &quot;
                {ExtraData?.SubscriptionTierName}&quot; tier for{" "}
                <span className="font-mono text-emerald-400">
                  {formatUSDCents(AmountUsdCents)}
                </span>{" "}
                / {ExtraData?.PaymentCadenceDays} days.
              </>
            ) : (
              <>
                subscribed to {_("your", true)} &quot;
                {ExtraData?.SubscriptionTierName}&quot; tier.
              </>
            )}
          </>
        ),
        push: {
          title: `You earned a ${formatUSDCents(AmountUsdCents)}/mo sub!`,
          body: `@${ActorUser.DisplayName}, worth ${formatUSDCents(ActorUser.TotalWealthUsdCents)}, just subscribed to your "${ExtraData?.SubscriptionTierName}" tier for ${formatUSDCents(AmountUsdCents)}/mo! You'll get paid every month.`,
          actions: [
            CommonPushNotificationActions.ViewProfile(notificationData),
          ],
        },
      };

    case NotificationSubCategory.UNLOCKABLE_CONTENT:
      return {
        title: `${formatUSDCents(AmountUsdCents)} post unlocked`,
        body: (
          <>
            paid{" "}
            <span className="font-mono text-emerald-400">
              {formatUSDCents(AmountUsdCents)}
            </span>{" "}
            to unlock {_("your", true)} post!
          </>
        ),
        push: {
          title: `You earned ${formatUSDCents(AmountUsdCents)} for a post unlock!`,
          body: `@${ActorUser.DisplayName}, worth ${formatUSDCents(ActorUser.TotalWealthUsdCents)}, just paid ${formatUSDCents(AmountUsdCents)} to unlock your post!${Post?.Body ? `Post preview: \n"${Post.Body}"` : ""}`,
          image: Post?.ImageUrls?.[0],
          actions: [
            CommonPushNotificationActions.ViewProfile(notificationData),
            CommonPushNotificationActions.ViewPost(notificationData),
          ],
        },
      };

    case NotificationSubCategory.COIN_PURCHASE:
      return {
        title: `${usdPurchaseAmount} coin purchase`,
        body: (
          <>
            bought{" "}
            <span className="font-mono text-emerald-400">
              {usdPurchaseAmount}
            </span>{" "}
            of {_("your", true)} coin!
          </>
        ),
        push: null,
      };

    case NotificationSubCategory.COIN_SALE:
      return {
        title: `${usdPurchaseAmount} coin sale`,
        body: (
          <>
            sold{" "}
            <span className="font-mono text-emerald-400">
              {usdPurchaseAmount}
            </span>{" "}
            of {_("your", true)} coin!
          </>
        ),
        push: null,
      };

    case NotificationSubCategory.RECEIVED_DESO:
      return {
        title: `${nanosToInteger(AmountNanosHex).toFixed(4)} $DESO received`,
        body: (
          <>
            sent {_`you`}{" "}
            <span className="font-mono text-emerald-400">
              {nanosToInteger(AmountNanosHex).toFixed(4)}
            </span>{" "}
            $DESO (
            <span className="font-mono text-emerald-400">
              {formatUSDCents(AmountUsdCents)}
            </span>{" "}
            $USD)!
          </>
        ),
        push: {
          title: `You just got ${nanosToInteger(AmountNanosHex).toFixed(4)} DESO`,
          body: `@${ActorUser.DisplayName} just sent you ${nanosToInteger(AmountNanosHex).toFixed(4)} DESO (~${formatUSDCents(AmountUsdCents)})!`,
          actions: [
            CommonPushNotificationActions.GoToWallet(notificationData),
            CommonPushNotificationActions.ViewProfile(notificationData),
          ],
        },
      };

    case NotificationSubCategory.RECEIVED_FOCUS:
      return {
        title: `${tokenNanosToInteger(AmountNanosHex).toFixed(2)} $FOCUS received`,
        body: (
          <>
            sent {_`you`}{" "}
            <span className="font-mono text-emerald-400">
              {tokenNanosToInteger(AmountNanosHex).toFixed(2)}
            </span>{" "}
            $FOCUS (
            <span className="font-mono text-emerald-400">
              {formatUSDCents(AmountUsdCents)}
            </span>{" "}
            $USD)!
          </>
        ),
        push: {
          title: `You just got ${nanosToInteger(AmountNanosHex).toFixed(4)} FOCUS`,
          body: `@${ActorUser.DisplayName} just sent you ${nanosToInteger(AmountNanosHex).toFixed(4)} FOCUS (~${formatUSDCents(AmountUsdCents)})!`,
          actions: [
            CommonPushNotificationActions.GoToWallet(notificationData),
            CommonPushNotificationActions.ViewProfile(notificationData),
          ],
        },
      };

    case NotificationSubCategory.RECEIVED_DUSDC:
      return {
        title: `${tokenNanosToInteger(AmountNanosHex).toFixed(2)} $USDC received`,
        body: (
          <>
            sent {_`you`}{" "}
            <span className="font-mono text-emerald-400">
              {formatUSDCents(AmountUsdCents)}
            </span>{" "}
            $USDC!
          </>
        ),
        push: {
          title: `You just got ${formatUSDCents(AmountUsdCents)} in USDC!`,
          body: `@${ActorUser.DisplayName} just sent you ${formatUSDCents(AmountUsdCents)} in USDC!`,
          actions: [
            CommonPushNotificationActions.GoToWallet(notificationData),
            CommonPushNotificationActions.ViewProfile(notificationData),
          ],
        },
      };

    case NotificationSubCategory.FOLLOW:
      return {
        title: "+1 follower",
        body: `followed ${_`you`}!`,
        push: {
          title: isActorWhale
            ? `A ${actorWhaleIdentifier} just followed you! - ${formatUSDCents(ActorUser.TotalWealthUsdCents)}`
            : `New follow! - ${formatUSDCents(ActorUser.TotalWealthUsdCents)}`,
          body: `@${ActorUser.DisplayName}, worth ${formatUSDCents(ActorUser.TotalWealthUsdCents)}, just followed you!`,
          actions: [
            CommonPushNotificationActions.ViewProfile(notificationData),
          ],
        },
      };

    case NotificationSubCategory.RECEIVED_POST_TO_EARN:
      return {
        title: `${formatUSDCents(AmountUsdCents)} Post to Earn revenue`,
        body: (
          <>
            sent {_`you`}{" "}
            <span className="font-mono text-emerald-400">
              {formatUSDCents(AmountUsdCents)}
            </span>{" "}
            in Post to Earn revenue for {_("your", true)} content!
          </>
        ),
        push: null,
      };

    case NotificationSubCategory.UNPAID_MESSAGE:
      return {
        title: `New message`,
        body: `sent ${_`you`} an unpaid message!`,
        push: null,
      };

    case NotificationSubCategory.ADDED_GROUP_CHAT:
      return {
        title: `New group chat`,
        body: `added ${_`you`} to ${getGroupChatNotificationSuffix(
          ExtraData?.GroupChatName,
        )}`,
        push: null,
      };

    case NotificationSubCategory.PROMOTION_OFFER:
      return {
        title: `${usdOfferAmount} promotion offer`,
        body: `sent ${_`you`} an ${usdOfferAmount} to share their post!`,
        push: null,
      };

    case NotificationSubCategory.PROMOTION_PERFORMANCE:
      return {
        title: `Promotion performance`,
        body: `${numAcceptedOffers} users accepted ${_("your", true)} promotion offers for an estimated ${numViewsEstimated} views!`,
        push: null,
      };

    case NotificationSubCategory.MENTION:
      return {
        title: `New mention`,
        body: `mentioned ${_`you`} in a post!`,
        push: {
          title: isActorWhale
            ? `A ${actorWhaleIdentifier} mentioned you! - ${formatUSDCents(ActorUser.TotalWealthUsdCents)}`
            : `New mention! - ${formatUSDCents(ActorUser.TotalWealthUsdCents)}`,
          body: `@${ActorUser.DisplayName}, worth ${formatUSDCents(ActorUser.TotalWealthUsdCents)}, just mentioned you in a post!${Post?.Body ? ` Post content: \n"${Post.Body}"` : ""}`,
          image: Post?.ImageUrls?.[0],
          actions: [
            CommonPushNotificationActions.ViewPost(notificationData),
            CommonPushNotificationActions.ViewProfile(notificationData),
          ],
        },
      };

    case NotificationSubCategory.POST_TIP: {
      const isPost = !!PostHashHex;
      return {
        title: `${formatUSDCents(AmountUsdCents)} ${isPost ? "post" : "profile"} tip`,
        body: (
          <>
            gave {_`you`} a{" "}
            <span className="font-mono text-emerald-400">
              {formatUSDCents(AmountUsdCents)}
            </span>{" "}
            <LuGem
              className="relative -top-px mr-1 inline-block text-muted"
              data-alternative="💎"
            />
            tip on {_("your", true)} {isPost ? "post" : "profile"}!
          </>
        ),
        push: {
          title: `You earned a ${formatUSDCents(AmountUsdCents)} post tip!`,
          body: `@${ActorUser.DisplayName}, worth ${formatUSDCents(ActorUser.TotalWealthUsdCents)}, just tipped you ${formatUSDCents(AmountUsdCents)} on your ${isPost ? "post" : "profile"}!${
            isPost && Post?.Body ? ` Post preview: \n"${Post.Body}"` : ""
          }`,
          image: Post?.ImageUrls?.[0],
          actions: [
            isPost && CommonPushNotificationActions.ViewPost(notificationData),
            CommonPushNotificationActions.ViewProfile(notificationData),
          ].filter(Boolean),
        },
      };
    }

    case NotificationSubCategory.POST_REPLY:
      return {
        title: `New comment`,
        body: `commented on ${_("your", true)} post!`,
        push: {
          title: isActorWhale
            ? `A ${actorWhaleIdentifier} just replied! - ${formatUSDCents(ActorUser.TotalWealthUsdCents)}`
            : `New reply! - ${formatUSDCents(ActorUser.TotalWealthUsdCents)}`,
          body: `@${ActorUser.DisplayName}, worth ${formatUSDCents(ActorUser.TotalWealthUsdCents)}, just replied to your post.${Post?.Body ? ` Post content: \n"${Post.Body}"` : ""}`,
          image: Post?.ImageUrls?.[0],
          actions: [
            CommonPushNotificationActions.ReplyComment(notificationData),
            CommonPushNotificationActions.ViewComment(notificationData),
            CommonPushNotificationActions.ViewProfile(notificationData),
          ],
        },
      };

    case NotificationSubCategory.POST_VIEW:
      return {
        title: "+1 view",
        body: `viewed ${_("your", true)} post!`,
        actions: [
          CommonPushNotificationActions.ViewPost(notificationData),
          CommonPushNotificationActions.ViewProfile(notificationData),
        ],
      };

    case NotificationSubCategory.POST_REPOST:
      return {
        title: "+1 repost",
        body: `reposted ${_("your", true)} post!`,
        push: {
          title: isActorWhale
            ? `A ${actorWhaleIdentifier} reposted you! - ${formatUSDCents(ActorUser.TotalWealthUsdCents)}`
            : `You got a repost! - ${formatUSDCents(ActorUser.TotalWealthUsdCents)}`,
          body: `@${ActorUser.DisplayName}, worth ${formatUSDCents(ActorUser.TotalWealthUsdCents)}, just reposted your post!${Post?.Body ? ` Post content: \n"${Post.Body}"` : ""}`,
          image: Post?.ImageUrls?.[0],
          actions: [
            CommonPushNotificationActions.ViewRePost(notificationData),
            CommonPushNotificationActions.ViewProfile(notificationData),
          ],
        },
      };

    case NotificationSubCategory.POST_QUOTE_REPOST:
      return {
        title: "+1 repost",
        body: `quoted ${_("your", true)} post!`,
        push: {
          title: isActorWhale
            ? `A ${actorWhaleIdentifier} quoted you! - ${formatUSDCents(ActorUser.TotalWealthUsdCents)}`
            : `You got a quote! - ${formatUSDCents(ActorUser.TotalWealthUsdCents)}`,
          body: `@${ActorUser.DisplayName}, worth ${formatUSDCents(ActorUser.TotalWealthUsdCents)}, just quoted your post!${Post?.Body ? ` Post content: \n"${Post.Body}"` : ""}`,
          image: Post?.ImageUrls?.[0],
          actions: [
            CommonPushNotificationActions.ViewRePost(notificationData),
            CommonPushNotificationActions.ViewProfile(notificationData),
          ],
        },
      };

    case NotificationSubCategory.POST_UPVOTE:
      return {
        title: "+1 upvote",
        body: `upvoted {_("your", true)} post!`,
        push: null,
      };

    case NotificationSubCategory.REACTION_LIKE:
    case NotificationSubCategory.REACTION_LAUGH:
    case NotificationSubCategory.REACTION_LOVE:
    case NotificationSubCategory.REACTION_DISLIKE:
    case NotificationSubCategory.REACTION_ANGRY:
    case NotificationSubCategory.REACTION_ASTONISHED:
    case NotificationSubCategory.REACTION_SAD:
      return {
        title: REACTION_EMOJI_UTF8_MAP[Subcategory],
        body: getReactionNotificationEmoji(Subcategory, _),
        push: {
          title: isActorWhale
            ? `A ${actorWhaleIdentifier} gave you a ${REACTION_EMOJI_UTF8_MAP[Subcategory]}! - ${formatUSDCents(ActorUser.TotalWealthUsdCents)}`
            : `You got a ${REACTION_EMOJI_UTF8_MAP[Subcategory]}! - ${formatUSDCents(ActorUser.TotalWealthUsdCents)}`,
          body: `"@${ActorUser.DisplayName}, worth ${formatUSDCents(ActorUser.TotalWealthUsdCents)}, just gave you a ${REACTION_EMOJI_UTF8_MAP[Subcategory]} on your post!${Post?.Body ? ` Post content: \n"${Post.Body}"` : ""}`,
          image: Post?.ImageUrls?.[0],
          actions: [
            CommonPushNotificationActions.ViewPost(notificationData),
            CommonPushNotificationActions.ViewProfile(notificationData),
          ],
        },
      };

    case NotificationSubCategory.POLL_RESPONSE:
      return {
        title: "+1 vote",
        body: (
          <>
            responded to {_("your", true)} poll with &quot;
            {ExtraData.PollResponse}&quot;.
          </>
        ),
        push: {
          title: isActorWhale
            ? `New ${actorWhaleIdentifier} poll response! - ${formatUSDCents(ActorUser.TotalWealthUsdCents)}`
            : `New poll response! - ${formatUSDCents(ActorUser.TotalWealthUsdCents)}`,
          body: `@${ActorUser.DisplayName}, worth ${formatUSDCents(ActorUser.TotalWealthUsdCents)}, just responded to your poll!${Post?.Body ? ` Poll content: \n"${Post.Body}"` : ""}`,
          actions: [
            CommonPushNotificationActions.ViewPost(notificationData),
            CommonPushNotificationActions.ViewProfile(notificationData),
          ],
        },
      };

    case NotificationSubCategory.ADDED_TO_LIST:
      return {
        title: "+1 follower",
        body: `followed ${_`you`} by adding ${_`you`} to their list!`,
        push: {
          title: isActorWhale
            ? `A ${actorWhaleIdentifier} added you to a list! - ${formatUSDCents(ActorUser.TotalWealthUsdCents)}`
            : `New follow! (${formatUSDCents(ActorUser.TotalWealthUsdCents)})`,
          body: `@${ActorUser.DisplayName}, worth ${formatUSDCents(ActorUser.TotalWealthUsdCents)}, just followed you by adding you to their list!`,
          actions: [
            CommonPushNotificationActions.ViewProfile(notificationData),
          ],
        },
      };

    case NotificationSubCategory.NEW_DIRECT_PAID_MESSAGE: {
      const number = formatUSDCents(
        Number(ExtraData.PendingAmountUsdCents ?? AmountUsdCents),
      );

      return {
        title: `${number} direct paid message`,
        body: (
          <>
            sent {_`you`} a new direct message for{" "}
            <span className="font-mono text-emerald-400">{number}</span>! View
            the message to receive the payment.
          </>
        ),
        push: {
          title: `@${ActorUser.DisplayName} sent you a ${formatUSDCents(AmountUsdCents)} paid message!`,
          body: `@${ActorUser.DisplayName}, worth ${formatUSDCents(ActorUser.TotalWealthUsdCents)}, just sent you a ${formatUSDCents(AmountUsdCents)} paid message! View the message to receive the payment.`,
          actions: [
            CommonPushNotificationActions.GoToMessages(notificationData),
            CommonPushNotificationActions.ViewProfile(notificationData),
          ],
        },
      };
    }

    case NotificationSubCategory.WHALE_POST_VIEW:
      return {
        title: "+1 view",
        body: `viewed ${_("your", true)} post!`,
        push: {
          title: isActorWhale
            ? `A ${actorWhaleIdentifier} just viewed your post! - ${formatUSDCents(ActorUser.TotalWealthUsdCents)}`
            : `Your post was viewed - ${formatUSDCents(ActorUser.TotalWealthUsdCents)}`,
          body: `@${ActorUser.DisplayName}, worth ${formatUSDCents(ActorUser.TotalWealthUsdCents)}, just viewed your post!${Post?.Body ? ` Post content: \n"${Post.Body}"` : ""}`,
          image: Post?.ImageUrls?.[0],
          actions: [
            CommonPushNotificationActions.ViewPost(notificationData),
            CommonPushNotificationActions.ViewProfile(notificationData),
          ],
        },
      };

    case NotificationSubCategory.POST_VIEW_THRESHOLD:
      return {
        title: "+1 view",
        body: (
          <>
            viewed {_("your", true)} post. This post now has {PostViewCount}{" "}
            views!
          </>
        ),
        push: null,
      };

    case NotificationSubCategory.COIN_PURCHASE_PLACED:
      return {
        title: Number(PriceUsd) > 0 ? "Buy limit order" : "Buy market order",
        body: (
          <>
            {Number(PriceUsd) > 0 ? (
              <>
                placed a limit order to buy{" "}
                <span className="font-mono text-emerald-400">
                  {formatDecimalValue(Quantity)}
                </span>{" "}
                of {_("your", true)} coin for{" "}
                <span className="font-mono text-emerald-400">
                  ${formatDecimalValue(PriceUsd, 4, 0)}
                </span>
                /coin.
              </>
            ) : (
              <>
                placed a market order to buy{" "}
                <span className="font-mono text-emerald-400">
                  {formatDecimalValue(Quantity)}
                </span>{" "}
                of {_("your", true)} coin.
              </>
            )}
          </>
        ),
        push: {
          title: `A ${formatUSDCents(+PriceUsd * +Quantity)} buy order was placed!`,
          body: `@${ActorUser.DisplayName}, worth ${formatUSDCents(ActorUser.TotalWealthUsdCents)}, just placed an order to buy ${formatUSDCents(+PriceUsd * +Quantity)} worth of your token for ${formatUSDCents(+PriceUsd)}/coin, @${User.DisplayName}!`,
          actions: [
            CommonPushNotificationActions.GoToTrade(notificationData),
            CommonPushNotificationActions.ViewProfile(notificationData),
          ],
        },
      };

    case NotificationSubCategory.COIN_SALE_PLACED: {
      const isLimitOrder = Number(PriceUsd) > 0;
      return {
        title: isLimitOrder ? "Sell limit order" : "Sell market order",
        body: (
          <>
            {isLimitOrder ? (
              <>
                {"placed a limit order to sell"}{" "}
                <span className="font-mono text-red-600">
                  {formatDecimalValue(Quantity, 2, 0)}
                </span>
                {` of ${_("your", true)} token for `}
                <span className="font-mono text-red-600">
                  ${formatDecimalValue(PriceUsd, 4, 0)}
                </span>
                {"/token."}
              </>
            ) : (
              <>
                {"placed a market order to sell"}{" "}
                <span className="font-mono text-red-600">
                  {formatDecimalValue(Quantity, 2, 0)}
                </span>{" "}
                {`of ${_("your", true)} token.`}
              </>
            )}
          </>
        ),
        push: {
          title: `A ${formatUSDCents(+PriceUsd * +Quantity)} sell order was placed!`,
          body: `@${ActorUser.DisplayName}, worth ${formatUSDCents(ActorUser.TotalWealthUsdCents)}, just placed an order to sell ${formatUSDCents(+PriceUsd * +Quantity)} worth of your token for ${formatUSDCents(+PriceUsd)}/coin, @${User.DisplayName}!`,
          actions: [
            CommonPushNotificationActions.GoToTrade(notificationData),
            CommonPushNotificationActions.ViewProfile(notificationData),
          ],
        },
      };
    }
    case NotificationSubCategory.COIN_PURCHASE_FILLED:
      return {
        title: `${QuantityBought} coin bought`,
        body: (
          <>
            bought{" "}
            <span className="font-mono text-emerald-400">{QuantityBought}</span>{" "}
            of {_("your", true)} token for{" "}
            <span className="font-mono text-emerald-400">
              ${formatDecimalValue(PriceUsd, 4, 0)}
            </span>
            /token.
          </>
        ),
        push: {
          title: `A $${abbreviateNumber(+QuantityBought * +PriceUsd)} buy order was filled`,
          actions: [
            CommonPushNotificationActions.GoToTrade(notificationData),
            CommonPushNotificationActions.ViewProfile(notificationData),
          ],
        },
      };

    case NotificationSubCategory.COIN_SALE_FILLED:
      return {
        title: `${QuantitySold} coin sold`,
        body: (
          <>
            sold{" "}
            <span className="font-mono text-emerald-400">{QuantitySold}</span>{" "}
            of {_("your", true)} token for{" "}
            <span className="font-mono text-emerald-400">${PriceUsd}</span>
            /token.
          </>
        ),
        actions: [
          CommonPushNotificationActions.ViewProfile(notificationData),
          CommonPushNotificationActions.GoToTrade(notificationData),
        ],
      };

    case NotificationSubCategory.COIN_TRADING_FEE:
      return {
        title: `${formatUSDCents(AmountUsdCents)} coin trading fee`,
        body: (
          <>
            traded {_("your", true)} coin and paid {_`you`} a fee of{" "}
            <span className="font-mono text-emerald-400">
              {formatUSDCents(AmountUsdCents)}
            </span>
            !
          </>
        ),
        push: {
          title: `You earned ${formatUSDCents(AmountUsdCents)} in trading fees!`,
          body: `You earned ${formatUSDCents(AmountUsdCents)} in trading fees on your token just now, @${User.DisplayName}!`,
          actions: [
            CommonPushNotificationActions.ViewEarnings(notificationData),
            CommonPushNotificationActions.GoToTrade(notificationData),
          ],
        },
      };

    case NotificationSubCategory.COIN_LOCKUP_YIELD:
      return {
        title: `${formatUSDCents(AmountUsdCents)} coin lockup yield`,
        body: (
          <>
            earned a yield of{" "}
            <span className="font-mono text-emerald-400">
              {formatUSDCents(AmountUsdCents)}
            </span>{" "}
            for locking up {_("your", true)} coin!
          </>
        ),
        push: {
          title: `You earned ${formatUSDCents(AmountUsdCents)} in yield!`,
          body: `You earned ${formatUSDCents(AmountUsdCents)} in yield for locking up your token!`,
          actions: [
            CommonPushNotificationActions.ViewEarnings(notificationData),
            CommonPushNotificationActions.GoToTrade(notificationData),
          ],
        },
      };

    case NotificationSubCategory.NEW_GROUP_PAID_MESSAGE:
      return {
        title: `New group paid message`,
        body: "sent a paid message in a group chat",
        push: null,
      };

    case NotificationSubCategory.NEW_DIRECT_UNPAID_MESSAGE:
      return {
        title: `New direct message`,
        body: `sent ${_`you`} a new direct message!`,
        push: {
          title: `@${ActorUser.DisplayName}${actorWhaleIdentifier} sent you a message - ${formatUSDCents(AmountUsdCents)}!`,
          body: `@${ActorUser.DisplayName}, worth ${formatUSDCents(AmountUsdCents)}, just sent you an unpaid message!`,
          actions: [
            CommonPushNotificationActions.ReplyMessage(notificationData),
            CommonPushNotificationActions.ViewProfile(notificationData),
            CommonPushNotificationActions.GoToMessages(notificationData),
          ],
        },
      };

    case NotificationSubCategory.NEW_GROUP_UNPAID_MESSAGE: {
      const othersUsersLabel =
        Number(ExtraData.NotificationGroupUserCount) > 0
          ? `and ${ExtraData.NotificationGroupUserCount} others `
          : "";
      const groupChatName = sanitizeMessagingGroupDisplayName(
        ExtraData.AccessGroupName,
      );

      return {
        title: `New group message`,
        body: (
          <>
            {groupChatName ? (
              <>
                @{ActorUser.DisplayName}
                {actorWhaleIdentifier} {othersUsersLabel}
                sent a new message to the &quot;
                {groupChatName}
                &quot; group!
              </>
            ) : (
              <>
                @{ActorUser.DisplayName}
                {actorWhaleIdentifier} {othersUsersLabel} sent a message to your
                group chat.
              </>
            )}
          </>
        ),
        push: {
          title: groupChatName || "New group message",
          // TODO missing data - will be resolved later
          //body: `{MISSING_MESSAGE_CONTENT}`,
          actions: [
            CommonPushNotificationActions.GoToMessages(notificationData),
          ],
        },
      };
    }

    case NotificationSubCategory.NEW_DIRECT_PAID_MESSAGE_REPLY:
      return {
        title: "New reply",
        body: <>replied to {_("your", true)} paid message!</>,
        push: {
          title: `You just got a reply to your message!`,
          body: `@${ActorUser.DisplayName}, worth ${formatUSDCents(AmountUsdCents)}, just replied to your paid message message! Open the app to see what they said.`,
          actions: [
            CommonPushNotificationActions.ViewProfile(notificationData),
            CommonPushNotificationActions.GoToMessages(notificationData),
          ],
        },
      };

    case NotificationSubCategory.NEW_GROUP_PAID_MESSAGE_REPLY:
      return {
        title: "New reply",
        body: "replied to a paid message in a group chat",
        push: null,
      };

    case NotificationSubCategory.NEW_DIRECT_UNPAID_MESSAGE_REPLY:
      return {
        title: "New reply",
        body: "replied to an unpaid message directly",
        push: null,
      };

    case NotificationSubCategory.NEW_GROUP_UNPAID_MESSAGE_REPLY:
      return {
        title: "New reply",
        body: "replied to an unpaid message in a group chat",
        push: null,
      };

    case NotificationSubCategory.ADDED_TO_GROUP_CHAT:
      return {
        title: "Group chat invite",
        body: (
          <>
            added {_`you`} to{" "}
            {getGroupChatNotificationSuffix(ExtraData.GroupChatName, "quoted")}!
          </>
        ),
        push: {
          title: isActorWhale
            ? `A ${actorWhaleIdentifier} just added you to a group chat!`
            : `You were added to a group chat!`,
          body: `@${ActorUser.DisplayName}, worth ${formatUSDCents(
            AmountUsdCents,
          )}, just added you to ${getGroupChatNotificationSuffix(
            ExtraData.GroupChatName,
          )}.`,
          actions: [
            CommonPushNotificationActions.GoToMessages(notificationData),
            CommonPushNotificationActions.ViewProfile(notificationData),
          ],
        },
      };

    case NotificationSubCategory.BOUNTY_REWARD_CLAIMED:
      return {
        title: `${formatUSDCents(AmountUsdCents)} bounty claimed`,
        body: (
          <>
            just sent {_`you`} the {capitalize(ExtraData.BountyPlatform)} bounty
            of{" "}
            <span className="font-mono text-emerald-400">
              {formatUSDCents(AmountUsdCents)}
            </span>
            !
          </>
        ),
        push: {
          title: `You earned a bounty of ${formatUSDCents(AmountUsdCents)}!`,
          body: `You earned ${formatUSDCents(AmountUsdCents)} for joining Focus! @${ActorUser.DisplayName} earned ${formatUSDCents(+ExtraData.ReferrerEarningsUsdCents)} for referring you.`,
          actions: null,
        },
      };

    case NotificationSubCategory.REFERRAL_EARNING:
      return {
        title: `${formatUSDCents(AmountUsdCents)} referral`,
        body: (
          <>
            {!!ExtraData.ReferralNotificationSubcategory &&
              resolveArrayTemplate(
                ReferralNotificationSubcategoryMessagesMap[
                  ExtraData.ReferralNotificationSubcategory
                ],
                [
                  `${_`you`} `,
                  <span key="revenue" className="font-mono text-emerald-400">
                    {formatUSDCents(AmountUsdCents)}
                  </span>,
                  ActorUser?.DisplayName,
                ],
              )}
            !
          </>
        ),
        get push() {
          switch (ExtraData.ReferralNotificationSubcategory) {
            case ReferralNotificationSubcategory.BOUNTY_REWARD_CLAIMED: {
              return {
                title: `You earned ${formatUSDCents(AmountUsdCents)} for a referral!`,
                body: `You got ${formatUSDCents(AmountUsdCents)} as a referral bonus because @${ActorUser.DisplayName} earned ${formatUSDCents(+ExtraData.ReferrerEarningsUsdCents)} as a bounty!`,
                actions: null,
              };
            }
            case ReferralNotificationSubcategory.NEW_SUBSCRIPTION: {
              return {
                title: `You earned ${formatUSDCents(AmountUsdCents)} in revshare!`,
                body: `You got ${formatUSDCents(AmountUsdCents)}/mo in subscription revshare because @${ActorUser.DisplayName} got a ${formatUSDCents(+ExtraData.ReferralEarningsUsdCents)}/mo subscriber! You'll get paid every month.`,
                actions: null,
              };
            }
            case ReferralNotificationSubcategory.UNLOCKABLE_CONTENT: {
              return {
                title: `You earned ${formatUSDCents(AmountUsdCents)} for a post unlock!`,
                body: `You got ${formatUSDCents(AmountUsdCents)} in unlockable content revshare because you referred @${ActorUser.DisplayName}, who just got ${+ExtraData.ReferralEarningsUsdCents} for a post unlock!`,
                actions: null,
              };
            }
            case ReferralNotificationSubcategory.COIN_TRADING_FEE: {
              return {
                title: `You earned ${formatUSDCents(AmountUsdCents)} in revshare!`,
                // TODO missing data - will be implemented later
                //body: `You got ${formatUSDCents(AmountUsdCents)} in trading fee revshare because you referred @${ActorUser.DisplayName}, who just made "JUST_MADE" in trading fees on their token!`,
                body: `You got ${formatUSDCents(AmountUsdCents)} in trading fee revshare because you referred @${ActorUser.DisplayName}!`,
                actions: null,
              };
            }
            case ReferralNotificationSubcategory.POST_TIP: {
              return {
                title: `You earned ${formatUSDCents(AmountUsdCents)} in revshare!`,
                body: `You got ${formatUSDCents(AmountUsdCents)} in trading fee revshare because you referred @${ActorUser.DisplayName}, who who got a ${+ExtraData.ReferralEarningsUsdCents} tip on their ${PostHashHex ? "post" : "profile"}!${
                  PostHashHex && Post?.Body
                    ? ` Post preview: \n"${Post.Body}"`
                    : ""
                }`,
                image: Post?.ImageUrls?.[0],
                actions: null,
              };
            }
            case ReferralNotificationSubcategory.PAID_MESSAGE_REDEMPTION: {
              return {
                title: `You earned ${formatUSDCents(AmountUsdCents)} in revshare!`,
                body: `You earned ${formatUSDCents(AmountUsdCents)} in pay-to-message revshare because you referred @${ActorUser.DisplayName}, who just got paid ${+ExtraData.ReferralEarningsUsdCents} for viewing a paid message!`,
                actions: null,
              };
            }
            case ReferralNotificationSubcategory.COIN_PURCHASE_PLACED:
            case ReferralNotificationSubcategory.COIN_PURCHASE_FILLED:
            case ReferralNotificationSubcategory.COIN_SALE_FILLED:
            case ReferralNotificationSubcategory.COIN_SALE_PLACED:
            case ReferralNotificationSubcategory.NEW_DIRECT_PAID_MESSAGE:
            default:
              return null;
          }
        },
      };

    case NotificationSubCategory.HIGH_VALUE_JOINING_REWARD:
      return {
        title: `${formatUSDCents(AmountUsdCents)} reward`,
        body: (
          <>
            joined Focus and was paid a bounty of{" "}
            <span className="font-mono text-emerald-400">
              {formatUSDCents(AmountUsdCents)}
            </span>
            !
          </>
        ),
        push: {
          title: `A ${actorWhaleIdentifier} just joined! - ${formatUSDCents(ActorUser.TotalWealthUsdCents)}`,
          body: `@${ActorUser.DisplayName}, worth ${formatUSDCents(ActorUser.TotalWealthUsdCents)}, just joined Focus and earned a ${formatUSDCents(AmountUsdCents)} joining bonus! Be the first to engage with their content and earn tips. You can adjust the threshold for this notification in Settings.`,
          actions: null,
        },
      };

    case NotificationSubCategory.PAID_MESSAGE_REDEMPTION:
      return {
        title: `${formatUSDCents(AmountUsdCents)} paid message`,
        body: (
          <>
            {_`You`} received{" "}
            <span className="font-mono text-emerald-400">
              {formatUSDCents(AmountUsdCents)}
            </span>{" "}
            for reading {ActorUser?.DisplayName}'s paid message!
          </>
        ),
        push: null,
      };

    case NotificationSubCategory.REFERRAL_JOINED_FOCUS:
      return {
        title: "New referral",
        body: (
          <>
            just joined Focus using {_("your", true)}{" "}
            {ExtraData.ReferralCodeUsed} referral code!{" "}
          </>
        ),
        push: null,
      };

    case NotificationSubCategory.SUBSCRIBEE_NEW_POST:
    case NotificationSubCategory.FOLLOWEE_NEW_POST:
      return {
        title: "New post",
        body: <>just created a new post!</>,
        push: {
          title: isActorWhale
            ? `New ${actorWhaleIdentifier} post! - ${formatUSDCents(ActorUser.TotalWealthUsdCents)}`
            : `New post! - ${formatUSDCents(ActorUser.TotalWealthUsdCents)}`,
          body: `@${ActorUser.DisplayName}, worth ${formatUSDCents(ActorUser.TotalWealthUsdCents)} and whom you follow, just posted!${Post?.Body ? ` Post content: \n"${Post.Body}"` : ""}`,
          image: Post?.ImageUrls?.[0],
          actions: [
            CommonPushNotificationActions.ViewPost(notificationData),
            CommonPushNotificationActions.ViewProfile(notificationData),
          ],
        },
      };

    default:
      return {
        title: `Unknown notification: ${Subcategory}`,
        body: "Please report this to the Focus team.",
        push: null,
      };
  }
};

export const NotificationSubCategoryIcons = {
  [NotificationSubCategory.REFERRAL]: LuMegaphone,
  [NotificationSubCategory.PAID_MESSAGE]: LuMail,
  [NotificationSubCategory.PAID_MESSAGE_REDEMPTION]: LuMail,
  [NotificationSubCategory.NEW_SUBSCRIPTION]: LuLink,
  [NotificationSubCategory.UNLOCKABLE_CONTENT]: LuUnlock,
  [NotificationSubCategory.COIN_PURCHASE]: LuCoins,
  [NotificationSubCategory.COIN_SALE]: LuCoins,
  [NotificationSubCategory.COIN_LOCKUP_YIELD]: LuCoins,
  [NotificationSubCategory.RECEIVED_DESO]: LuHelpingHand,
  [NotificationSubCategory.RECEIVED_FOCUS]: LuHelpingHand,
  [NotificationSubCategory.RECEIVED_DUSDC]: LuHelpingHand,
  [NotificationSubCategory.RECEIVED_POST_TO_EARN]: LuCoins,
  [NotificationSubCategory.UNPAID_MESSAGE]: LuMailOpen,
  [NotificationSubCategory.ADDED_GROUP_CHAT]: LuUserPlus,
  [NotificationSubCategory.PROMOTION_OFFER]: LuCoins,
  [NotificationSubCategory.PROMOTION_PERFORMANCE]: LuCoins,
  [NotificationSubCategory.FOLLOW]: LuUserPlus,
  [NotificationSubCategory.MENTION]: LuAtSign,
  [NotificationSubCategory.POST_TIP]: LuGem,
  [NotificationSubCategory.POST_REPLY]: LuReply,
  [NotificationSubCategory.POST_VIEW]: LuBarChart2,
  [NotificationSubCategory.POST_REPOST]: LuRepeat,
  [NotificationSubCategory.POST_QUOTE_REPOST]: LuQuote,
  [NotificationSubCategory.POST_UPVOTE]: LuThumbsUp,
  [NotificationSubCategory.REACTION_LIKE]: LuThumbsUp,
  [NotificationSubCategory.REACTION_LAUGH]: LuSmile,
  [NotificationSubCategory.REACTION_LOVE]: LuHeart,
  [NotificationSubCategory.REACTION_DISLIKE]: LuThumbsDown,
  [NotificationSubCategory.REACTION_ANGRY]: LuAngry,
  [NotificationSubCategory.REACTION_ASTONISHED]: LuSkull,
  [NotificationSubCategory.REACTION_SAD]: LuAnnoyed,
  [NotificationSubCategory.POLL_RESPONSE]: LuListTodo,
  [NotificationSubCategory.ADDED_TO_LIST]: LuUserPlus,
  [NotificationSubCategory.NEW_DIRECT_PAID_MESSAGE]: LuMail,
  [NotificationSubCategory.WHALE_POST_VIEW]: LuView,
  [NotificationSubCategory.POST_VIEW_THRESHOLD]: LuView,
  [NotificationSubCategory.COIN_PURCHASE_PLACED]: LuReceipt,
  [NotificationSubCategory.COIN_SALE_PLACED]: LuReceipt,
  [NotificationSubCategory.COIN_PURCHASE_FILLED]: LuCircleDollarSign,
  [NotificationSubCategory.COIN_SALE_FILLED]: LuCircleDollarSign,
  [NotificationSubCategory.COIN_TRADING_FEE]: LuPiggyBank,
  [NotificationSubCategory.NEW_GROUP_PAID_MESSAGE]: LuMessageCircle,
  [NotificationSubCategory.NEW_DIRECT_UNPAID_MESSAGE]: LuMail,
  [NotificationSubCategory.NEW_GROUP_UNPAID_MESSAGE]: LuMails,
  [NotificationSubCategory.NEW_DIRECT_PAID_MESSAGE_REPLY]: LuMessageSquare,
  [NotificationSubCategory.NEW_GROUP_PAID_MESSAGE_REPLY]: LuMessageCircle,
  [NotificationSubCategory.NEW_DIRECT_UNPAID_MESSAGE_REPLY]: LuMessageCircle,
  [NotificationSubCategory.NEW_GROUP_UNPAID_MESSAGE_REPLY]: LuMessageCircle,
  [NotificationSubCategory.ADDED_TO_GROUP_CHAT]: LuUsers,
  [NotificationSubCategory.HIGH_VALUE_JOINING_REWARD]: LuDollarSign,
  [NotificationSubCategory.REFERRAL_COIN_TRADING_FEE]: LuPiggyBank,
  [NotificationSubCategory.REFERRAL_EARNING]: LuDollarSign,
  [NotificationSubCategory.BOUNTY_REWARD_CLAIMED]: LuDollarSign,
  [NotificationSubCategory.REFERRAL_JOINED_FOCUS]: LuUserPlus,
  [NotificationSubCategory.FOLLOWEE_NEW_POST]: LuNewspaper,
  [NotificationSubCategory.SUBSCRIBEE_NEW_POST]: LuNewspaper,
};
